import { Component } from '@theme/component';

/**
 * @typedef {object} PersonalizeNameRefs
 * @property {HTMLInputElement} mainInput - The read-only trigger input on the page.
 * @property {HTMLDialogElement} dialog - The native dialog element.
 * @property {HTMLInputElement} modalInput - The input inside the modal.
 */

/**
 * A custom element that manages the personalization name input and modal.
 * @extends Component<PersonalizeNameRefs>
 */
export class PersonalizeNameComponent extends Component {
  requiredRefs = ['mainInput', 'dialog', 'modalInput'];

  /**
   * Opens the personalization modal and syncs the current value.
   * @param {Event} [event]
   */
  openModal(event) {
    if (event) {
      event.preventDefault();
    }

    const { dialog, mainInput, modalInput } = this.refs;

    if (!dialog || dialog.open) return;

    if (modalInput && mainInput) {
      modalInput.value = mainInput.value;
    }

    // Set scroll lock on HTML document element
    document.documentElement.setAttribute('scroll-lock', '');

    // Show native dialog modal
    dialog.showModal();

    // Focus on the input inside the modal and select text
    if (modalInput) {
      modalInput.focus();
      modalInput.select();
    }

    // Attach listeners for backdrop clicks and cancel (Escape key) events
    dialog.addEventListener('click', this.#handleBackdropClick);
    dialog.addEventListener('cancel', this.#handleCancel);
  }

  /**
   * Closes the personalization modal with a transition.
   */
  closeModal() {
    const { dialog } = this.refs;

    if (!dialog || !dialog.open) return;

    // Clean up event listeners immediately
    dialog.removeEventListener('click', this.#handleBackdropClick);
    dialog.removeEventListener('cancel', this.#handleCancel);

    // Remove scroll lock
    document.documentElement.removeAttribute('scroll-lock');

    // Add class to trigger CSS closing animation
    dialog.classList.add('personalize-modal--closing');

    let isClosed = false;
    const finishClose = () => {
      if (isClosed) return;
      isClosed = true;
      dialog.close();
      dialog.classList.remove('personalize-modal--closing');
    };

    // Transition/animation end listener
    dialog.addEventListener('animationend', finishClose, { once: true });
    
    // Safety fallback timeout matching the CSS transition duration
    setTimeout(finishClose, 250);
  }

  /**
   * Saves the value from the modal input back to the main input and closes the modal.
   * @param {Event} [event]
   */
  saveValue(event) {
    if (event) {
      event.preventDefault();
    }
    const { mainInput, modalInput } = this.refs;
    if (modalInput && mainInput) {
      mainInput.value = modalInput.value;

      // Dispatch standard input/change events for theme reactivity
      mainInput.dispatchEvent(new Event('input', { bubbles: true }));
      mainInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.closeModal();
  }

  /**
   * Handles keyboard navigation on the main trigger input.
   * Opens the modal when Enter or Space is pressed.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openModal();
    }
  }

  /**
   * Closes the dialog if clicked outside the content area (on the backdrop).
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (event) => {
    const { dialog } = this.refs;
    if (event.target === dialog) {
      this.closeModal();
    }
  };

  /**
   * Overrides native immediately close behavior of the Escape key to allow exit animations.
   * @param {Event} event
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeModal();
  };
}

if (!customElements.get('personalize-name-component')) {
  customElements.define('personalize-name-component', PersonalizeNameComponent);
}

